import { useState, useEffect, forwardRef, useContext } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Slide,
  Box,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  FilledInput,
  InputAdornment,
  Menu,
  MenuItem,
  Avatar,
  ListItemAvatar,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { EventContext } from "../../../../../contexts/EventContext";
import request from "../../../../../utilities/requests";
import { toast } from "../../../../../utilities/SnackBarUtils";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PitchConfiguration = ({ open, close, onSave }) => {
  const { pairingEvent } = useContext(EventContext);
  const [pitches, setPitches] = useState([]);
  const [title, setTitle] = useState("");
  const [voteScreenTitle, setVoteScreenTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchVal, setSearchVal] = useState("");
  const [users, setUsers] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedPitch, setSelectedPitch] = useState(null);
  const [editUrlOpen, setEditUrlOpen] = useState(false);
  const [editUrl, setEditUrl] = useState("");
  const [editSummary, setEditSummary] = useState("");

  useEffect(() => {
    if (!open) return;
    getPitches();
  }, [open]);

  const getPitches = async () => {
    const result = await request(
      `pitches/?eventId=${pairingEvent?.event?._id}`,
    );
    if (result.success) {
      setPitches(result.pitches);
      setVoteScreenTitle(result.voteScreenTitle);
      setTitle(result.title);
    }
  };

  const handleSearch = async () => {
    if (searchVal.length < 3) return;
    setLoading(true);
    const body = {
      value: searchVal,
    };
    const result = await request("users/search", "post", body);
    if (result.success) {
      setUsers(result.users);
      // console.log(result.users);
    }
    setLoading(false);
  };

  const handleAdd = async (user) => {
    let order = 1;

    if (pitches.length > 0) {
      const maxOrder = Math.max(...pitches.map((p) => p.order || 0));
      order = maxOrder + 1;
    }

    const body = {
      eventId: pairingEvent?.event?._id,
      userId: user._id,
      order: order,
    };

    const result = await request("pitchesadmin/save", "post", body);

    if (result.success) {
      //   setPitches((prev) => [...prev, result.pitch]);
      getPitches();
      setUsers([]);
      setSearchVal("");
    }
  };

  const handleMenuOpen = (event, pitch) => {
    setMenuAnchor(event.currentTarget);
    setSelectedPitch(pitch);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedPitch(null);
  };

  const handleEditUrlOpen = () => {
    if (!selectedPitch) return;
    setEditUrl(selectedPitch.url || "");
    setEditSummary(selectedPitch.summary || "");
    setEditUrlOpen(true);

    // close the menu but preserve selectedPitch
    setMenuAnchor(null);
  };

  const handleEditUrlClose = () => {
    setEditUrlOpen(false);
  };

  const handleEditUrlSave = async () => {
    if (!selectedPitch) return;

    const body = {
      eventId: pairingEvent?.event?._id,
      userId: selectedPitch.userId,
      url: editUrl,
      summary: editSummary,
    };

    const result = await request("pitchesadmin/save", "post", body);

    if (result.success) {
      await getPitches();
      setEditUrlOpen(false);
    }
  };

  const handleMoveUp = async () => {
    if (!selectedPitch) return;

    const sorted = [...pitches].sort((a, b) => a.order - b.order);
    const index = sorted.findIndex((p) => p._id === selectedPitch._id);

    if (index <= 0) {
      handleMenuClose();
      return;
    }

    const current = sorted[index];
    const previous = sorted[index - 1];

    await request("pitchesadmin/save", "post", {
      eventId: pairingEvent?.event?._id,
      userId: current.userId,
      order: previous.order,
    });

    await request("pitchesadmin/save", "post", {
      eventId: pairingEvent?.event?._id,
      userId: previous.userId,
      order: current.order,
    });

    await getPitches();
    handleMenuClose();
  };

  const handleMoveDown = async () => {
    if (!selectedPitch) return;

    const sorted = [...pitches].sort((a, b) => a.order - b.order);
    const index = sorted.findIndex((p) => p._id === selectedPitch._id);

    if (index === -1 || index === sorted.length - 1) {
      handleMenuClose();
      return;
    }

    const current = sorted[index];
    const next = sorted[index + 1];

    await request("pitchesadmin/save", "post", {
      eventId: pairingEvent?.event?._id,
      userId: current.userId,
      order: next.order,
    });

    await request("pitchesadmin/save", "post", {
      eventId: pairingEvent?.event?._id,
      userId: next.userId,
      order: current.order,
    });

    await getPitches();
    handleMenuClose();
  };

  const handleRemove = async () => {
    if (!selectedPitch) return;

    const body = {
      eventId: pairingEvent?.event?._id,
      userId: selectedPitch.userId,
    };

    const result = await request("pitchesadmin/delete", "post", body);

    if (result.success) {
      getPitches();
    }

    handleMenuClose();
  };

  const handleSaveTitle = async () => {
    const body = {
      eventId: pairingEvent?.event?._id,
      title: title,
      voteScreenTitle: voteScreenTitle,
    };

    const result = await request("pitchesadmin/save", "post", body);

    if (result.success) {
      getPitches();
      toast.success("Update saved successfully");
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={close}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={close}>
            <CloseIcon />
          </IconButton>

          <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
            Pitch Configuration
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 4 }}>
        {/* Existing Pitches */}
        {pitches.length === 0 ? (
          <Typography variant="body1" sx={{ mb: 3 }}>
            No pitches have been set up for this event yet
          </Typography>
        ) : (
          <>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Current Pitches
            </Typography>

            <List sx={{ mb: 5 }}>
              {[...pitches]
                .sort((a, b) => a.order - b.order)
                .map((pitch) => (
                  <ListItem
                    key={pitch._id}
                    disablePadding
                    sx={{
                      py: 1.5,
                      px: 1,
                      mb: 1.5,
                      borderRadius: 2,
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={(e) => handleMenuOpen(e, pitch)}
                        sx={{ mr: 1 }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={pitch.profileImage || ""}
                        sx={{ width: 56, height: 56 }}
                      >
                        {(pitch.firstName || "").charAt(0)}
                      </Avatar>
                    </ListItemAvatar>

                    <ListItemText
                      primary={`${pitch.order}. ${pitch.firstName || ""} ${pitch.lastName || ""}`}
                      secondary={pitch.email}
                      primaryTypographyProps={{
                        variant: "h6",
                        sx: { mb: 0.5 },
                      }}
                      secondaryTypographyProps={{
                        variant: "body1",
                        color: "text.secondary",
                      }}
                      sx={{ ml: 1.5 }}
                    />
                  </ListItem>
                ))}
            </List>
          </>
        )}

        <Divider sx={{ mb: 1 }} />

        {/* Add Pitch */}
        <Box
          sx={{ p: 4, mx: "auto", width: { md: "50%", sm: "75%", xs: "100%" } }}
        >
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Add a New Pitch
          </Typography>

          <FormControl variant="filled" sx={{ mt: 2, mb: 2, width: "100%" }}>
            <InputLabel>Email</InputLabel>
            <FilledInput
              type="text"
              value={searchVal}
              onChange={(e) => setSearchVal(e.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleSearch}
                    disabled={loading || searchVal.length < 3}
                  >
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
          </FormControl>

          {users.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Search Results
              </Typography>

              <List>
                {users.map((user) => (
                  <ListItem key={user._id} disablePadding>
                    <ListItemButton onClick={() => handleAdd(user)}>
                      <ListItemText
                        primary={`${user.fullName || ""}`}
                        secondary={user.email}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Box>
        <Divider sx={{ mb: 1 }} />
        <Box
          sx={{ p: 4, mx: "auto", width: { md: "50%", sm: "75%", xs: "100%" } }}
        >
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            Pitch/Vote Page Title (User screen)
          </Typography>

          <TextField
            fullWidth
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button variant="contained" onClick={handleSaveTitle}>
              Save
            </Button>
          </Box>
        </Box>
        <Box
          sx={{ p: 4, mx: "auto", width: { md: "50%", sm: "75%", xs: "100%" } }}
        >
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            Pitch/Vote Page Title (Result Display Screen)
          </Typography>

          <TextField
            fullWidth
            label="Vote Screen Title"
            value={voteScreenTitle}
            onChange={(e) => setVoteScreenTitle(e.target.value)}
            
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button variant="contained" onClick={handleSaveTitle}>
              Save
            </Button>
          </Box>
        </Box>
      </Box>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMoveUp}>Move Up</MenuItem>
        <MenuItem onClick={handleMoveDown}>Move Down</MenuItem>
        <Divider />
        <MenuItem onClick={handleEditUrlOpen}>Edit</MenuItem>
        <Divider />
        <MenuItem onClick={handleRemove}>Remove</MenuItem>
      </Menu>

      <Dialog
        open={editUrlOpen}
        onClose={handleEditUrlClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Pitch</DialogTitle>

        <DialogContent>
          <TextField
            fullWidth
            label="URL"
            value={editUrl}
            onChange={(e) => setEditUrl(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Summary"
            value={editSummary}
            onChange={(e) => {
              if (e.target.value.length <= 140) {
                setEditSummary(e.target.value);
              }
            }}
            margin="normal"
            multiline
            rows={3}
            helperText={`${editSummary.length}/140 characters`}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleEditUrlClose}>Cancel</Button>
          <Button variant="contained" onClick={handleEditUrlSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PitchConfiguration;
